import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./Dialog";
import { Button } from "../Button";
import { PrimaryButton } from "../PrimaryButton";
import { Loader } from "../Loader";

export interface CheckboxConfirmDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  title: string;
  description?: string;
  checkboxText?: string;
  requireCheckbox?: boolean;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  children?: React.ReactNode;
}

export function CheckboxConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  checkboxText,
  requireCheckbox = false,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  children,
}: CheckboxConfirmDialogProps) {
  const [isChecked, setIsChecked] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const canConfirm = !requireCheckbox || isChecked;

  React.useEffect(() => {
    if (!open) {
      setIsChecked(false);
      setIsLoading(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!canConfirm || isLoading) return;

    setIsLoading(true);
    try {
      await onConfirm?.();
      onOpenChange?.(false);
    } catch (error) {
      console.error("Confirm action failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="px-4 py-5 sm:p-6">
          {children}

          {checkboxText && (
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="text-sm text-slate-600">{checkboxText}</span>
            </label>
          )}
        </div>

        <DialogFooter>
          <PrimaryButton
            type="button"
            className="sm:!w-fit"
            onClick={handleConfirm}
            disabled={!canConfirm || isLoading}
          >
            {isLoading ? <Loader small /> : confirmText}
          </PrimaryButton>
          <Button type="button" onClick={handleCancel} disabled={isLoading}>
            {cancelText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
